import { defineStore } from 'pinia'
import { apiClient } from '@/shared/api/client'
import { useUserActivity } from '@/shared/composables/useUserActivity'

// ── Constantes des auto-transitions de statut ────────────────────────────
// Ces durées matchent la spec produit :
//   - wrap_up : max 30 s, puis available
//   - available : 10 min sans activité (souris, clavier, scroll, route, appel)
//                 → busy (« Indisponible »)
//   - busy : 30 min de plus → offline
//
// `WRAP_UP_MAX_S` n'est pas conditionné à l'activité : un agent qui finit
// sa fiche en 25 s passe quand même en available à T+30 s. C'est la règle
// produit. Si on veut tolérer la frappe, il suffira d'utiliser idleSeconds
// ici aussi (cf. AVAILABLE_IDLE_S).
const WRAP_UP_MAX_S = 30
// Inactivité avant bascule « Indisponible » : 10 min (et non 3) — un agent
// disponible qui lit/prépare un dossier sans bouger la souris ne doit pas
// être basculé trop tôt.
const AVAILABLE_IDLE_S = 600
const BUSY_TO_OFFLINE_S = 1800
// Période de vérification des conditions — assez fine pour ne pas dépasser
// d'une grande marge la cible (1 s = pire cas 1 s de retard).
const TICK_MS = 1000

// Heartbeat d'activité : on signale au serveur que l'agent interagit
// (souris/clavier/navigation) au plus une fois par minute, et SEULEMENT s'il
// a été actif récemment. Sans ce signal, le balayage serveur considère la
// session inactive et bascule l'agent en hors ligne au bout du délai de son
// statut — c'est ce qui rattrape les apps fermées brutalement.
const ACTIVITY_PING_INTERVAL_S = 60
const ACTIVITY_RECENT_S = 90
let lastActivityPing = 0

// Heartbeat de CONNEXION : signale au serveur que l'app est ouverte et
// connectée, INDÉPENDAMMENT de l'activité souris/clavier (≠ activity-heartbeat).
// Envoyé toutes les 30 s tant que l'agent n'est pas hors ligne. Quand l'app se
// ferme / le réseau tombe, ce ping s'arrête → le serveur bascule l'agent
// « Disponible » en « Indisponible » après 3,5 min (plus d'appels routés dans
// le vide). NON conditionné à l'inactivité NI à l'onglet caché (une app
// minimisée reste connectée).
const CONNECTION_PING_INTERVAL_S = 30
let lastConnectionPing = 0

export type AgentStatusKey =
  | 'available'
  | 'busy'
  | 'pause'
  | 'lunch'
  | 'training'
  | 'meeting'
  | 'back_office'
  | 'in_call'
  | 'wrap_up'
  | 'offline'

export interface StatusDef {
  key: AgentStatusKey
  label: string
  description: string
  color: string
  bg: string
  icon: string
  canReceiveCalls: boolean
  selectable: boolean
}

export const STATUS_DEFS: Record<AgentStatusKey, StatusDef> = {
  available:   { key: 'available',   label: 'Disponible',      description: 'Prêt à recevoir des appels',     color: '#10B981', bg: '#D1FAE5', icon: '🟢', canReceiveCalls: true,  selectable: true  },
  in_call:     { key: 'in_call',     label: 'En appel',         description: 'Communication en cours',          color: '#06B6D4', bg: '#CFFAFE', icon: '📞', canReceiveCalls: false, selectable: false },
  wrap_up:     { key: 'wrap_up',     label: 'Travail post-appel', description: 'Saisie de fiche / notes',         color: '#8B5CF6', bg: '#EDE9FE', icon: '📝', canReceiveCalls: false, selectable: false },
  back_office: { key: 'back_office', label: 'Back office',      description: 'Tâches administratives',          color: '#3B82F6', bg: '#DBEAFE', icon: '📂', canReceiveCalls: false, selectable: true  },
  pause:       { key: 'pause',       label: 'Pause',            description: 'Pause courte',                     color: '#F59E0B', bg: '#FEF3C7', icon: '☕', canReceiveCalls: false, selectable: true  },
  lunch:       { key: 'lunch',       label: 'Déjeuner',         description: 'Pause repas',                      color: '#F97316', bg: '#FFEDD5', icon: '🍽️', canReceiveCalls: false, selectable: true  },
  training:    { key: 'training',    label: 'Formation',        description: "Session d'apprentissage",          color: '#EC4899', bg: '#FCE7F3', icon: '🎓', canReceiveCalls: false, selectable: true  },
  meeting:     { key: 'meeting',     label: 'Réunion',          description: 'En réunion équipe',                color: '#14B8A6', bg: '#CCFBF1', icon: '👥', canReceiveCalls: false, selectable: true  },
  busy:        { key: 'busy',        label: 'Indisponible',     description: 'Ne pas déranger',                  color: '#EF4444', bg: '#FEE2E2', icon: '⛔', canReceiveCalls: false, selectable: true  },
  offline:     { key: 'offline',     label: 'Hors ligne',       description: 'Déconnecté',                       color: '#9CA3AF', bg: '#F3F4F6', icon: '⚫', canReceiveCalls: false, selectable: true  },
}

export const SELECTABLE_STATUSES: AgentStatusKey[] = [
  'available', 'back_office', 'pause', 'lunch', 'training', 'meeting', 'busy', 'offline',
]

// Timer global (un seul pour toute l'app, pas un par instance de store) qui
// tick chaque seconde et déclenche les transitions automatiques. On le pose
// hors du state Pinia pour ne pas le sérialiser et éviter les warnings de
// reactivity sur un handle setInterval.
let autoTransitionTimer: ReturnType<typeof setInterval> | null = null
// Ancrage temporel pour la transition « depuis combien de temps en X ».
// On utilise une horloge locale au lieu de `changedAt` du serveur : ça nous
// dispense des problèmes d'horloge décalée et fait que la transition reste
// précise même si la requête `change()` met du temps à répondre.
let statusEnteredAt = Date.now()

export const useAgentStatusStore = defineStore('agent_status', {
  state: () => ({
    current: 'offline' as AgentStatusKey,
    changedAt: new Date().toISOString(),
    note: '',
    loading: false,
    inactiveAlert: false,
    inactiveSeconds: 0,
    // Main levée : l'agent demande l'aide d'un superviseur (visible orange en
    // supervision). Réinitialisée par le serveur au passage hors ligne.
    handRaised: false,
  }),
  getters: {
    def(state): StatusDef { return STATUS_DEFS[state.current] },
    canReceiveCalls(state): boolean { return STATUS_DEFS[state.current].canReceiveCalls },
  },
  actions: {
    async load() {
      try {
        const r = await apiClient.get('/api/pexitalk/monitoring/agent-status/')
        const prev = this.current
        this.current = r.data.status
        this.changedAt = r.data.changed_at
        this.note = r.data.note || ''
        this.inactiveAlert = !!r.data.inactive_alert
        this.inactiveSeconds = r.data.inactive_seconds || 0
        this.handRaised = !!r.data.hand_raised
        if (prev !== this.current) statusEnteredAt = Date.now()
        this.startAutoTransitions()
      } catch {
        // ignore — offline default
      }
    },
    async change(status: AgentStatusKey, note = '', reason?: string) {
      // Si le backend rejette ou tarde, on enclenche déjà l'horloge locale :
      // pour les transitions auto on veut éviter qu'un agent reste en wrap_up
      // 30 s + N s si le PATCH est lent.
      // `reason` n'est passé que par les auto-transitions du timer ci-dessous
      // (le serveur le valide contre une liste blanche) ; un clic manuel n'en
      // envoie pas → journalisé 'manual'.
      const prev = this.current
      this.loading = true
      try {
        const body: Record<string, unknown> = { status, note }
        if (reason) body.reason = reason
        const r = await apiClient.post('/api/pexitalk/monitoring/agent-status/', body)
        this.current = r.data.status
        this.changedAt = r.data.changed_at
        this.note = r.data.note || ''
      } finally {
        this.loading = false
      }
      if (prev !== this.current) statusEnteredAt = Date.now()
      this.startAutoTransitions()
    },

    /** Lève / baisse la main pour demander l'aide d'un superviseur. Mise à jour
     *  optimiste, repli si le serveur refuse. */
    async toggleHand() {
      const next = !this.handRaised
      this.handRaised = next
      try {
        const r = await apiClient.post('/api/pexitalk/monitoring/hand-raise/', { raised: next })
        this.handRaised = !!r.data.hand_raised
      } catch {
        this.handRaised = !next
      }
    },

    /**
     * Démarre (ou ré-amorce) le timer global qui évalue les conditions
     * d'auto-transition chaque seconde. Idempotent — peut être rappelé
     * sans dupliquer le timer.
     *
     * Les règles évaluées :
     *  - wrap_up : > 30 s écoulées depuis l'entrée dans cet état → available
     *  - available : > 10 min sans activité utilisateur (mousemove, clavier,
     *    scroll, focus, route) → busy
     *  - busy : > 30 min écoulées depuis l'entrée → offline
     */
    startAutoTransitions() {
      if (autoTransitionTimer) return
      const activity = useUserActivity()
      autoTransitionTimer = setInterval(() => {
        const sinceStatus = (Date.now() - statusEnteredAt) / 1000
        const idle = activity.idleSeconds()

        // Heartbeat d'activité serveur : seulement si l'agent est en ligne,
        // récemment actif, et au plus une fois par minute. Quand l'app est
        // fermée ou l'agent vraiment inactif, le ping s'arrête → le serveur
        // bascule l'agent hors ligne après le délai de son statut.
        if (this.current !== 'offline' && idle <= ACTIVITY_RECENT_S) {
          const nowS = Date.now() / 1000
          if (nowS - lastActivityPing >= ACTIVITY_PING_INTERVAL_S) {
            lastActivityPing = nowS
            apiClient.post('/api/pexitalk/monitoring/activity-heartbeat/').catch(() => {})
          }
        }

        // Heartbeat de CONNEXION : INCONDITIONNEL (ni inactivité ni onglet
        // caché), tant que l'agent n'est pas hors ligne. Prouve que l'app est
        // vivante ; s'il s'arrête (app fermée / réseau coupé), le serveur
        // bascule un agent « Disponible » en « Indisponible » après 3,5 min.
        if (this.current !== 'offline') {
          const nowConn = Date.now() / 1000
          if (nowConn - lastConnectionPing >= CONNECTION_PING_INTERVAL_S) {
            lastConnectionPing = nowConn
            apiClient.post('/api/pexitalk/monitoring/connection-heartbeat/').catch(() => {})
          }
        }

        // Les bascules sur INACTIVITÉ (available→busy, busy→offline) ne doivent
        // PAS être pilotées par un onglet CACHÉ : son `idle` grimpe (aucun
        // évènement souris/clavier en arrière-plan) même si l'agent travaille
        // dans un AUTRE onglet/fenêtre. Sans ce garde, un 2ᵉ onglet ouvert
        // poussait le statut available→busy→offline en boucle, contre l'onglet
        // actif où l'agent se remet « Disponible » → statut qui « clignote » en
        // quelques secondes. Seul l'onglet VISIBLE décide ; si aucun n'est
        // visible (app minimisée), le balayage serveur (30 min) reste le filet.
        const hidden = typeof document !== 'undefined' && document.hidden

        if (this.current === 'wrap_up' && sinceStatus >= WRAP_UP_MAX_S) {
          // Fire-and-forget — si le serveur est down, on retentera au tick
          // suivant via statusEnteredAt qui ne s'avance pas tant que le
          // change() n'a pas réussi. `reason` distingue ces bascules auto des
          // clics manuels dans le journal Temps agent.
          this.change('available', '', 'auto_wrap_up').catch(() => {})
        } else if (!hidden && this.current === 'available' && idle >= AVAILABLE_IDLE_S) {
          this.change('busy', '', 'auto_idle').catch(() => {})
        } else if (!hidden && this.current === 'busy' && idle >= BUSY_TO_OFFLINE_S) {
          // Offline sur INACTIVITÉ réelle (et non temps passé en busy) : un
          // agent « Indisponible » qui travaille (souris/clavier) reste en
          // ligne ; seul un agent sans aucune interaction bascule.
          this.change('offline', '', 'auto_offline').catch(() => {})
        }
      }, TICK_MS)
    },

    /** Arrête le timer global — appelé au logout pour ne pas continuer à
     *  fire des change() après que `auth` ait nettoyé le token (sinon on
     *  spamme des 401 jusqu'à la fermeture de l'app). */
    stopAutoTransitions() {
      if (autoTransitionTimer) {
        clearInterval(autoTransitionTimer)
        autoTransitionTimer = null
      }
    },
  },
})
